// @ts-check
import Handlebars from 'handlebars';
import {
    any,
    chain,
    filter,
    has,
    identity,
    join,
    multiply,
    pipe,
    prop,
    values,
    split,
    map,
    trim,
    reject,
    isEmpty,
    when,
} from 'ramda';
import {
    dateTimeConverter,
    convertTime,
    displayDate,
    displayDateAndTime,
    displayDateTime,
    getUrlDate,
    stripUrlDate,
} from '@sinclair-digital/sbg-storyline-components/utils';

export const isStory = view => () => !!view.storyId;

export const tagWeight = multiply(100);

export const getSubsections = pipe(
    chain(prop('subsections')),
    filter(identity),
    filter(has('name')),
    chain(subsection => [subsection.name, getSubsections([subsection])]),
    filter(identity),
    join(' '),
);

const httprgx = new RegExp('http://', 'g');
const wwwrgx = new RegExp('www.(?!com$)', 'g');
export const removeWebPrefixes = url => (url ? url.replace(httprgx, '').replace(wwwrgx, '') : url);

export const getSaveMessage = model => {
    const savedDateTime = model.savedDateTime;

    if (savedDateTime) {
        return `saved ${dateTimeConverter(savedDateTime)}`;
    } else {
        return '';
    }
};

export const getSaveString = view => `saved ${dateTimeConverter(view.model.get('savedDateTime'))}`;

export const versionStatusText = view => () =>
    `Version ${(view.model.get('version') || '').toString()} ${getSaveString(view)}`;

// It's important that this pass through its context (I think) so it needs to be a function not => - ADM 1.4.16
export const ifEq = function(a, b, opts) {
    return a === b ? opts.fn(this) : opts.inverse(this);
};

export const warn = renditions => {
    let warning = false;

    if (renditions) {
        if (typeof renditions === 'object') {
            warning = any(identity, values(renditions).map(prop('warn')));
        } else {
            warning = true;
        }
    }

    return warning ? 'sda-flag flag-visible' : '';
};

// Leaving this here rather than moving to SDK to minimize backbone inclusion in SDK - ADM 9.10.17
export const media = view => () => (view.model.get('images') && view.uploadStarted && 'media-show') || undefined;

export const checkOrder = (setOrder, index) => setOrder || index + 1;

export const eachVersion = (array, obj) =>
    Array.isArray(array)
        ? array
            .map((item, i) =>
                obj.fn({
                    ...item,
                    live: i === 0,
                    count: array.length - i,
                }),
            )
            .join('')
        : '';

export const isModelLive = view => () => view.model.isLive();

export const getTargetUuid = view => () => {
    const target = view.model.get('primaryTarget');
    return (target && target.get('site').get('uuid')) || null;
};

export const isStorySaved = view => () => !!view.model.get('uuid');

export const wasPublished = view => () => view.model.wasPublished();

export const getReadOnlyValue = view => () => {
    const readOnly = view.model.get('readOnly');
    return readOnly ?? false;
};

export const isSyndicationAvailable = view => () => {
    if (view.model.get('uuid')) {
        const primaryTarget = view.model.get('primaryTarget');
        if (!primaryTarget) return false;
        const propertyUuid = primaryTarget.get('site').get('uuid');
        const canUserPublish =
            view.userModel.userHasPermissionSet('package.publish.alert', propertyUuid) || view._syndicationPermissions;

        return canUserPublish;
    }

    return false;
};

/**
 * Concatenates chunks of text together. Will exclude chunks that throws `joined` over the character limit.
 * @param {number} characterLimit character limit to not exceed
 * @param {string[]} chunks of text
 * @returns {string} joined
 */
const joinText = (delimeter, characterLimit, chunks) => {
    let joined = '';

    for (const chunk of chunks) {
        if (joined.length + chunk.length > characterLimit) {
            joined = joined.substring(0, joined.length - delimeter.length);
            break;
        } else {
            joined += chunk + delimeter;
        }
    }

    return joined;
};

/**
 * Splits text into sentences.
 * @param {string} text to split
 * @returns {string[]}
 */
const splitSentences = text => {
    const sentenceSplit = /(.+?[.!?\r\n]+)/;
    const endsWithWord = t => /\w$/.test(t);
    const appendPeriod = t => t + '.';
    const addFinalPunctuation = when(endsWithWord, appendPeriod);

    return pipe(
        split(sentenceSplit),
        reject(isEmpty),
        map(trim),
        map(addFinalPunctuation),
    )(text);
};

/**
 * Generates a summary from text by grabbing all sentences before the character limit is exeeded.
 * @param {{characterLimit: number, text: string}}
 * @returns {string}
 */
export const generateSummary = ({characterLimit, text}) => {
    const sentences = splitSentences(text);
    return joinText(' ', characterLimit, sentences);
};

// eslint-disable-next-line max-statements
export const registerStoryHelpers = view => {
    Handlebars.registerHelper('checkOrder', checkOrder);
    Handlebars.registerHelper('convertTime', convertTime);
    Handlebars.registerHelper('displayDate', displayDate);
    Handlebars.registerHelper('displayDateAndTime', displayDateAndTime);
    Handlebars.registerHelper('displayDateTime', displayDateTime);
    Handlebars.registerHelper('each_version', eachVersion);
    Handlebars.registerHelper('getSaveMessage', getSaveMessage);
    Handlebars.registerHelper('getSubsections', getSubsections);
    Handlebars.registerHelper('getTargetUuid', getTargetUuid(view));
    Handlebars.registerHelper('getUrlDate', getUrlDate(view));
    Handlebars.registerHelper('if_eq', ifEq);
    Handlebars.registerHelper('isStory', isStory(view));
    Handlebars.registerHelper('isStoryLive', isModelLive(view));
    Handlebars.registerHelper('media', media(view));
    Handlebars.registerHelper('removeWebPrefixes', removeWebPrefixes);
    Handlebars.registerHelper('storyIsSaved', isStorySaved(view));
    Handlebars.registerHelper('stripUrlDate', stripUrlDate);
    Handlebars.registerHelper('tagWeight', tagWeight);
    Handlebars.registerHelper('versionStatusText', versionStatusText(view));
    Handlebars.registerHelper('warn', warn);
    Handlebars.registerHelper('wasPublished', wasPublished(view));
    Handlebars.registerHelper('getReadOnlyValue', getReadOnlyValue(view));
};
